import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/core/add';
import { Box, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { messages } from '@databases/intl/messages';
import QuickCalculationsRow from '@databases/quick-calc/QuickCalculationsRow';
import { LEFT_ACTIONS_MID_WIDTH } from '@databases/table-resizer/constants';
import { type Database, type DatabaseEntry } from '@databases/types/database';
import { type DatabaseView } from '@databases/types/database/view';
import { NUMBERED_COLUMN_WIDTH } from '@databases/utils/constants';
import React, { useState, type CSSProperties, type FunctionComponent } from 'react';
import { useIntl } from 'react-intl-next';

import useAddEntry from './use-add-entry';

const COMPACT_BUTTON_COLLAPSED_WIDTH = NUMBERED_COLUMN_WIDTH;
const COMPACT_BUTTON_EXPANDED_WIDTH = 160;
export const ROW_HEIGHT = 44;
const ROW_BORDER_AND_PADDING_OFFSET = 5;
export const ROW_OUTER_HEIGHT = ROW_HEIGHT - ROW_BORDER_AND_PADDING_OFFSET;

interface NativeEmbedCondensedBottomRowProps {
  addEntryButtonRef: React.RefObject<HTMLButtonElement>;
  canEditContent: boolean;
  columnWidths: (number | undefined)[];
  containerWidth: number;
  contentId: string;
  database: Database;
  onAdd?: (entry: DatabaseEntry) => void;
  shouldShowEntryPrefix: (view: DatabaseView) => boolean;
}

const rowContainerStyles = xcss({
  position: 'relative',
  borderWidth: 'border.width',
  borderLeftWidth: '0px',
  borderStyle: 'solid',
  borderColor: 'color.border',
  backgroundColor: 'elevation.surface',
  borderBottomRightRadius: 'radius.large',
});

const expandedOverlayStyles = xcss({
  position: 'relative',
  backgroundColor: 'elevation.surface',
  width: '100%',
  zIndex: '1',
  transition: 'opacity 150ms ease-in-out',
  cursor: 'pointer',
});

const expandedOverlayVisibleStyles = xcss({
  opacity: 1,
});

const expandedOverlayHiddenStyles = xcss({
  opacity: 0,
  pointerEvents: 'none',
});

const expandedOverlayHoverTintStyles = xcss({
  position: 'absolute',
  insetBlockStart: 'space.0',
  insetInlineStart: 'space.0',
  width: '100%',
  height: '100%',
  backgroundColor: 'color.background.neutral.subtle.hovered',
});

const compactButtonContainerStyles = xcss({
  position: 'sticky',
  overflow: 'hidden',
  backgroundColor: 'elevation.surface',
  display: 'flex',
  alignItems: 'stretch',
  transition: 'box-shadow 150ms ease-in-out',
  zIndex: '1',
  borderLeftWidth: 'border.width',
  borderLeftStyle: 'solid',
  borderLeftColor: 'color.border',
  borderBottomWidth: 'border.width',
  borderBottomStyle: 'solid',
  borderBottomColor: 'color.border',
  borderBottomLeftRadius: 'radius.large',
});

const hoverBgColor = token('color.background.neutral.subtle.hovered', '#091E420F');

/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
const overlayDynamicStyles: CSSProperties = {
  height: ROW_HEIGHT,
  marginTop: -ROW_HEIGHT,
};

const getCompactButtonContainerDynamicStyles = (isExpanded: boolean): CSSProperties => ({
  height: ROW_HEIGHT + 1,
  marginTop: -ROW_HEIGHT,
  marginBottom: -1,
  left: LEFT_ACTIONS_MID_WIDTH,
  width: isExpanded ? COMPACT_BUTTON_EXPANDED_WIDTH : COMPACT_BUTTON_COLLAPSED_WIDTH,
  boxShadow: `-3px 3px 0 3px ${token('elevation.surface', '#FFFFFF')}`,
});
/* eslint-enable @atlaskit/design-system/ensure-design-token-usage */

const addEntryButtonBaseStyles: CSSProperties = {
  borderRadius: 0,
  height: '100%',
  alignItems: 'center',
  justifyContent: 'flex-start',
  minWidth: COMPACT_BUTTON_COLLAPSED_WIDTH,
  transition: 'background-color 180ms ease-in-out',
};

const buttonTextStyles = xcss({
  opacity: 1,
  transition: 'opacity 180ms ease-in-out 80ms',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  marginLeft: 'space.negative.400',
});

const buttonTextHiddenStyles = xcss({
  opacity: 0,
  transition: 'opacity 180ms ease-in-out 0ms',
});

const NativeEmbedCondensedBottomRow: FunctionComponent<
  NativeEmbedCondensedBottomRowProps
> = ({
  addEntryButtonRef,
  canEditContent,
  columnWidths,
  containerWidth,
  contentId,
  database,
  onAdd,
  shouldShowEntryPrefix,
}) => {
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const handleClick = useAddEntry(contentId, onAdd);
  const { formatMessage } = useIntl();

  return (
    <Box xcss={rowContainerStyles} onMouseLeave={() => setIsButtonHovered(false)}>
      <QuickCalculationsRow
        compact
        database={database}
        columnWidths={columnWidths}
        containerWidth={containerWidth}
        canEditContent={canEditContent}
        shouldShowEntryPrefix={shouldShowEntryPrefix}
      />
      {canEditContent && (
        <>
          <Box
            xcss={[
              expandedOverlayStyles,
              isButtonHovered
                ? expandedOverlayVisibleStyles
                : expandedOverlayHiddenStyles,
            ]}
            style={overlayDynamicStyles}
            onClick={handleClick}
            // Keyboard / screen reader access is provided by the Button below; this overlay is a
            // mouse-only click target to extend the hit area across the row, hence the role="none"
            role="none"
          >
            <Box xcss={expandedOverlayHoverTintStyles} />
          </Box>
          <Box
            xcss={compactButtonContainerStyles}
            style={getCompactButtonContainerDynamicStyles(isButtonHovered)}
            testId="native-embed-compact-add-entry-container"
            onMouseEnter={() => setIsButtonHovered(true)}
          >
            <Button
              ref={addEntryButtonRef}
              appearance="subtle"
              onClick={handleClick}
              iconBefore={
                <AddIcon
                  spacing="spacious"
                  color={token('color.text.subtlest', '#626F86')}
                  label=""
                />
              }
              spacing="compact"
              shouldFitContainer
              testId="table-layout-add-entry"
              style={{
                ...addEntryButtonBaseStyles,
                backgroundColor: isButtonHovered ? hoverBgColor : undefined,
              }}
            >
              <Box xcss={[buttonTextStyles, !isButtonHovered && buttonTextHiddenStyles]}>
                <Text color="color.text.subtlest" size="medium">
                  {formatMessage(messages['table-layout.add-entry'])}
                </Text>
              </Box>
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default NativeEmbedCondensedBottomRow;
